/**
 * DynamicConfigSelector Component
 * Model and configuration selection with dynamic options from API
 */

import { Select } from '@/components/ui/Select';
import { ModelSelector } from './ModelSelector';
import type { ImageModelInfo, ImageGenerationConfig } from '@/types/playground';
import { useI18n } from '@/i18n';

interface DynamicConfigSelectorProps {
  models: ImageModelInfo[];
  config: ImageGenerationConfig;
  currentModel: ImageModelInfo | undefined;
  operationMode: 'generate' | 'edit' | 'fuse';
  onModelChange: (modelId: string) => void;
  onSizeChange: (size: string) => void;
  onAspectRatioChange: (ratio: string) => void;
  onQualityChange: (quality: string | null) => void;
  onWebSearchChange: (enabled: boolean) => void;
  onBackgroundChange?: (background: string | null) => void;
  compact?: boolean;
  isLoading?: boolean;
}

/**
 * Get aspect ratio key for i18n
 */
function getAspectRatioKey(ratio: string): string {
  const keyMap: Record<string, string> = {
    '1:1': 'aspectRatioSquare',
    '3:4': 'aspectRatioPortrait',
    '4:3': 'aspectRatioLandscape',
    '9:16': 'aspectRatioVertical',
    '16:9': 'aspectRatioWide',
    '2:3': 'aspectRatioTallPortrait',
    '3:2': 'aspectRatioTallLandscape',
    '4:5': 'aspectRatioSocial',
    '5:4': '', // No special label, use raw value
    '21:9': 'aspectRatioUltraWide',
    '1:2': 'aspectRatioTall',
    '2:1': 'aspectRatioBanner',
  };
  return keyMap[ratio] || '';
}

/**
 * Get size key for i18n
 */
function getSizeKey(size: string): string {
  const keyMap: Record<string, string> = {
    'auto': 'sizeAuto',
  };
  return keyMap[size] || '';
}

/**
 * Get quality key for i18n
 */
function getQualityKey(quality: string): string {
  const keyMap: Record<string, string> = {
    'standard': 'qualityStandard',
    'hd': 'qualityHD',
    'low': 'qualityLow',
    'medium': 'qualityMedium',
    'high': 'qualityHigh',
    'auto': 'qualityAuto',
  };
  return keyMap[quality] || '';
}

export function DynamicConfigSelector({
  models,
  config,
  currentModel,
  operationMode,
  onModelChange,
  onSizeChange,
  onAspectRatioChange,
  onQualityChange,
  onWebSearchChange,
  onBackgroundChange,
  compact = false,
  isLoading = false,
}: DynamicConfigSelectorProps) {
  const { t } = useI18n();

  // Helper functions that use i18n
  const getAspectRatioLabel = (ratio: string): string => {
    const key = getAspectRatioKey(ratio);
    if (key) {
      return t(`playground.labels.${key}`);
    }
    return ratio;
  };

  const getSizeLabel = (size: string): string => {
    const key = getSizeKey(size);
    if (key) {
      return t(`playground.labels.${key}`);
    }
    // For numeric sizes like 1K, 2K, 4K, keep as is with dimension info
    const sizeLabels: Record<string, string> = {
      '1K': '1K (1024px)',
      '2K': '2K (2048px)',
      '4K': '4K (4096px)',
      '1024x1024': '1024×1024',
      '1024x1792': '1024×1792',
      '1792x1024': '1792×1024',
      '512x512': '512×512',
      '256x256': '256×256',
    };
    return sizeLabels[size] || size;
  };

  const getQualityLabel = (quality: string): string => {
    const key = getQualityKey(quality);
    if (key) {
      return t(`playground.labels.${key}`);
    }
    return quality;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-gray-200 rounded-lg"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-10 bg-gray-200 rounded-lg"></div>
          <div className="h-10 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex flex-wrap gap-3 items-center">
        {/* Model selector (compact) */}
        <div className="min-w-[160px]">
          <ModelSelector
            models={models}
            selectedModel={config.model}
            onModelChange={onModelChange}
            operationMode={operationMode}
            compact
          />
        </div>

        {/* Size selector */}
        {currentModel && currentModel.sizes.length > 0 && (
          <div className="flex rounded-lg overflow-hidden border border-gray-200">
            {currentModel.sizes.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => onSizeChange(size)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  config.size === size
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        )}

        {/* Aspect ratio selector (compact dropdown) */}
        {currentModel && currentModel.aspect_ratios.length > 0 && (
          <select
            value={config.aspect_ratio}
            onChange={(e) => onAspectRatioChange(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500"
          >
            {currentModel.aspect_ratios.map((ratio) => (
              <option key={ratio} value={ratio}>
                {ratio}
              </option>
            ))}
          </select>
        )}

        {/* Web search toggle */}
        {currentModel?.supports_web_search && (
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={config.web_search}
              onChange={(e) => onWebSearchChange(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-gray-700">{t('playground.labels.enableWebSearch')}</span>
          </label>
        )}
      </div>
    );
  }

  // Full config panel
  return (
    <div className="space-y-5">
      {/* Model Selection */}
      <ModelSelector
        models={models}
        selectedModel={config.model}
        onModelChange={onModelChange}
        operationMode={operationMode}
      />

      {/* Size and Aspect Ratio */}
      {currentModel && (
        <div className="grid grid-cols-2 gap-4">
          {/* Size Selection */}
          {currentModel.sizes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('playground.labels.resolution')}
              </label>
              <div className="flex flex-wrap rounded-lg overflow-hidden border border-gray-200">
                {currentModel.sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => onSizeChange(size)}
                    className={`flex-1 min-w-[60px] px-3 py-2 text-sm font-medium transition-colors ${
                      config.size === size
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {getSizeLabel(size)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Aspect Ratio Selection */}
          {currentModel.aspect_ratios.length > 0 && (
            <Select
              label={t('playground.labels.aspectRatio')}
              value={config.aspect_ratio}
              onChange={(e) => onAspectRatioChange(e.target.value)}
              options={currentModel.aspect_ratios.map((ratio) => ({
                value: ratio,
                label: getAspectRatioLabel(ratio),
              }))}
            />
          )}
        </div>
      )}

      {/* Quality and Background (if supported) */}
      {currentModel && (currentModel.quality_options.length > 0 || currentModel.supports_transparency) && (
        <div className="grid grid-cols-2 gap-4">
          {/* Quality Selection */}
          {currentModel.quality_options.length > 0 && (
            <Select
              label={t('playground.labels.quality')}
              value={config.quality || ''}
              onChange={(e) => onQualityChange(e.target.value || null)}
              options={[
                { value: '', label: t('playground.labels.defaultOption') },
                ...currentModel.quality_options.map((q) => ({
                  value: q,
                  label: getQualityLabel(q),
                })),
              ]}
            />
          )}

          {/* Background (for transparency support) */}
          {currentModel.supports_transparency && onBackgroundChange && (
            <Select
              label={t('playground.labels.background')}
              value={config.background || ''}
              onChange={(e) => onBackgroundChange(e.target.value || null)}
              options={[
                { value: '', label: t('playground.labels.defaultOption') },
                { value: 'transparent', label: t('playground.labels.transparent') },
                { value: 'white', label: t('playground.labels.white') },
                { value: 'auto', label: t('playground.labels.auto') },
              ]}
            />
          )}
        </div>
      )}

      {/* Web Search Toggle */}
      {currentModel?.supports_web_search && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            id="web_search"
            checked={config.web_search}
            onChange={(e) => onWebSearchChange(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <div>
            <label htmlFor="web_search" className="text-sm font-medium text-gray-700 cursor-pointer">
              {t('playground.labels.enableWebSearch')}
            </label>
            <p className="text-xs text-gray-500">
              {t('playground.labels.webSearchDescription')}
            </p>
          </div>
        </div>
      )}

      {/* Model capabilities summary */}
      {currentModel && (
        <div className="text-xs text-gray-500 flex flex-wrap gap-2">
          {currentModel.max_n > 1 && (
            <span className="px-2 py-1 bg-gray-100 rounded">
              {t('playground.labels.maxGenerate').replace('{count}', String(currentModel.max_n))}
            </span>
          )}
          {operationMode === 'fuse' && currentModel.max_images_fuse > 0 && (
            <span className="px-2 py-1 bg-gray-100 rounded">
              {t('playground.labels.maxFuse').replace('{count}', String(currentModel.max_images_fuse))}
            </span>
          )}
          {currentModel.output_formats.length > 1 && (
            <span className="px-2 py-1 bg-gray-100 rounded">
              {t('playground.labels.output')}: {currentModel.output_formats.join(', ')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
